import { PatientSuggestion } from '@/api'
import { ReactComponent as InfoIcon } from '@/assets/info.svg'
import { ReactComponent as UnmatchedIcon } from '@/assets/unmatched-icon.svg'
import { Banner, ButtonLink, PrimaryButton } from '@/components'
import { paths } from '@/routes/paths'
import { usePatientSuggestionsList, useWithdrawPatientSuggestion } from '@/services'
import { changeDateFormat, useNavigateWithFromState } from '@/utils'
import { Trans, useTranslation } from 'react-i18next'

const useMessages = () => {
  const { t } = useTranslation()

  return {
    text: t('onboardingPage.step0Text'),
    button: t('onboardingPage.step0Button'),
    pending: t('suggestPatientPage.pending', { returnObjects: true }),
    rejected: t('suggestPatientPage.rejected', { returnObjects: true }),
  }
}

const PendingSuggestion = ({ suggestion }: { suggestion: PatientSuggestion }) => {
  const messages = useMessages()
  const withdrawMutation = useWithdrawPatientSuggestion()
  const patientName = `${suggestion.first_name} ${suggestion.last_name}`

  return (
    <>
      <Banner
        title={messages.pending.title}
        icon={<InfoIcon fill="var(--primary)" height={24} width={24} className="shrink-0" />}
      >
        <Trans
          i18nKey={'suggestPatientPage.pending.text'}
          values={{ patient_name: patientName, station_name: suggestion.station }}
        />
      </Banner>
      <span className="text-b90 text-textS text-center">
        {patientName} · {changeDateFormat('-', '.', suggestion.date_of_birth)}
      </span>
      <ButtonLink
        className="underline"
        type="button"
        disabled={withdrawMutation.isLoading}
        onClick={() => withdrawMutation.mutate(suggestion.id)}
      >
        {messages.pending.withdraw}
      </ButtonLink>
    </>
  )
}

const RejectedSuggestion = ({ suggestion }: { suggestion: PatientSuggestion }) => {
  const messages = useMessages()
  const withdrawMutation = useWithdrawPatientSuggestion()

  return (
    <>
      <Banner title={messages.rejected.title}>
        <Trans
          i18nKey={'suggestPatientPage.rejected.text'}
          values={{
            patient_name: `${suggestion.first_name} ${suggestion.last_name}`,
            station_name: suggestion.station,
          }}
        />
      </Banner>
      <ButtonLink
        className="underline"
        type="button"
        disabled={withdrawMutation.isLoading}
        onClick={() => withdrawMutation.mutate(suggestion.id)}
      >
        {messages.rejected.dismiss}
      </ButtonLink>
    </>
  )
}

export const UnmatchedPage = () => {
  const navigate = useNavigateWithFromState()
  const messages = useMessages()
  const { data: suggestions = [] } = usePatientSuggestionsList()

  const pendingSuggestion = suggestions.find(({ status }) => status === 'PENDING')
  const rejectedSuggestion = suggestions.find(
    ({ status }) => status === 'DECLINED' || status === 'EXPIRED',
  )

  return (
    <div className="flex flex-col items-center justify-center h-full px-4 child:max-w-[488px] gap-4">
      <UnmatchedIcon className="shrink-0" width={64} height={64} />
      {pendingSuggestion ? (
        <PendingSuggestion suggestion={pendingSuggestion} />
      ) : (
        <>
          {rejectedSuggestion && <RejectedSuggestion suggestion={rejectedSuggestion} />}
          <div className="text-textL text-center font-normal">{messages.text}</div>
        </>
      )}
      <PrimaryButton block onClick={() => navigate(`/${paths.onboarding}`)}>
        {messages.button}
      </PrimaryButton>
    </div>
  )
}
